'use client';

import * as React from 'react';
import * as P from '@radix-ui/react-popover';
import { cn } from './cn';

export const PopoverRoot = P.Root;
export const PopoverTrigger = P.Trigger;
export const PopoverAnchor = P.Anchor;
export const PopoverClose = P.Close;

export interface PopoverContentProps extends React.ComponentPropsWithoutRef<typeof P.Content> {
  /** Draw the little pointer at the anchor. */
  arrow?: boolean;
  /** Removes the default padding — for pickers that manage their own grid. */
  bare?: boolean;
}

/**
 * Floating surface anchored to a trigger. `align="start"` follows the reading
 * direction because Radix resolves it against `dir` — wrap the app in
 * `<UIProvider dir="rtl">` (or Radix's `DirectionProvider`) once.
 */
export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof P.Content>,
  PopoverContentProps
>(function PopoverContent(
  { className, align = 'start', sideOffset = 8, collisionPadding = 12, arrow, bare, children, ...props },
  ref,
) {
  return (
    <P.Portal>
      <P.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          'z-(--z-popover) rounded-lg border border-line bg-surface shadow-lg',
          'max-h-(--radix-popover-content-available-height) w-max max-w-[min(92vw,22rem)] overflow-y-auto',
          'origin-(--radix-popover-content-transform-origin)',
          'data-[state=open]:animate-scale-in data-[state=closed]:animate-scale-out',
          !bare && 'p-3',
          className,
        )}
        {...props}
      >
        {children}
        {arrow ? <P.Arrow className="fill-surface stroke-line" width={12} height={6} /> : null}
      </P.Content>
    </P.Portal>
  );
});

export interface PopoverProps extends PopoverContentProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger: React.ReactNode;
  modal?: boolean;
}

export function Popover({ open, defaultOpen, onOpenChange, trigger, modal, ...content }: PopoverProps) {
  return (
    <P.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
      <P.Trigger asChild>{trigger}</P.Trigger>
      <PopoverContent {...content} />
    </P.Root>
  );
}
